import { Link } from 'react-router-dom';
import { X, GitCompare, Trash2, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { useCompare } from '../config/compareContext';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { PriceDisplay } from './PriceDisplay';

export default function CompareBar() {
  const { compareItems, compareCount, maxItems, removeFromCompare, clearCompare, openCompareModal } = useCompare();
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';

  if (compareCount === 0) return null;

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-40 border-t shadow-2xl transition-transform duration-300 ${
        isDarkMode 
          ? 'bg-[#141414] border-white/10' 
          : 'bg-white border-gray-200'
      }`}
      dir={isAr ? 'rtl' : 'ltr'}
      data-testid="compare-bar"
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center gap-4">
          {/* Header */}
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1E9BD9] to-[#0284C7] flex items-center justify-center">
              <GitCompare className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'قارن الأرقام' : 'Compare Numbers'}
              </h3>
              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {compareCount} / {maxItems} {isAr ? 'أرقام' : 'numbers'}
              </p>
            </div>
          </div>

          {/* Items Preview */}
          <div className="flex-1 flex items-center gap-2 overflow-x-auto scrollbar-hide px-2">
            {compareItems.map((item) => (
              <div 
                key={item.id}
                className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-xl border transition-all ${
                  isDarkMode 
                    ? 'bg-white/5 border-white/10' 
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="text-center">
                  <div className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`} dir="ltr">
                    {item.number}
                  </div>
                  <div className="text-xs text-[#1E9BD9] font-medium">
                    <PriceDisplay price={item.price} size="sm" />
                  </div>
                </div>
                <button 
                  onClick={() => removeFromCompare(item.id)}
                  className={`p-1 rounded-full transition-colors ${
                    isDarkMode 
                      ? 'hover:bg-red-500/20 text-gray-400 hover:text-red-400' 
                      : 'hover:bg-red-50 text-gray-400 hover:text-red-500'
                  }`}
                  data-testid={`remove-compare-${item.id}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}

            {/* Empty Slots */}
            {Array.from({ length: maxItems - compareCount }).map((_, index) => (
              <div 
                key={`empty-${index}`}
                className={`flex-shrink-0 w-28 h-14 rounded-xl border-2 border-dashed flex items-center justify-center ${
                  isDarkMode 
                    ? 'border-white/10 text-gray-600' 
                    : 'border-gray-200 text-gray-300'
                }`}
              >
                <span className="text-xs">+</span>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={clearCompare}
              className={`${
                isDarkMode 
                  ? 'border-white/20 text-gray-400 hover:text-red-400 hover:border-red-400/50' 
                  : 'border-gray-200 text-gray-500 hover:text-red-500 hover:border-red-300'
              }`}
              data-testid="clear-compare-btn"
            >
              <Trash2 className="h-4 w-4 me-1" />
              {isAr ? 'مسح' : 'Clear'}
            </Button>
            
            <Button
              onClick={openCompareModal}
              disabled={compareCount < 2}
              className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:from-[#0284C7] hover:to-[#1E9BD9] text-white px-6"
              data-testid="open-compare-modal-btn"
            >
              <GitCompare className="h-4 w-4 me-2" />
              {isAr ? 'قارن الآن' : 'Compare Now'}
              <ArrowLeft className={`h-4 w-4 ms-2 ${!isAr && 'rotate-180'}`} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
